import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'

import {
  getV2ResultTableData,
  rowsHaveValues,
} from '../../../../modes/attendance/attendanceV2ResultTable'
import {AttendanceScope, AttendanceV2Payload} from '../../../../modes/attendance/attendanceV2Types'
import {AttendanceSummary} from '../../../../modes/attendance/attendanceV2Types'
import {
  buildV2ResultExportPayload,
  resolveV2AttendanceModeLabel,
} from '../../../../modes/attendance/buildV2ResultExportPayload'
import {
  buildV2AttendanceVisualSummary,
  hasV2AttendanceChart,
} from '../../../../modes/attendance/buildV2VisualSummary'
import AttendanceReportExportButtons from '../AttendanceReportExportButtons'
import AttendanceV2DataTable, {INITIAL_VISIBLE_ROWS} from './AttendanceV2DataTable'
import AttendanceV2NameListSection from './AttendanceV2NameListSection'
import AttendanceV2NoDataNotice from './AttendanceV2NoDataNotice'
import AttendanceV2SummaryKpis, {shouldShowV2SummaryHeader} from './AttendanceV2SummaryKpis'
import AttendanceV2VisualSummary from './AttendanceV2VisualSummary'

const LOAD_MORE_BATCH = 8

type AttendanceV2ResultViewProps = {
  payload: AttendanceV2Payload
  theme: 'light' | 'dark'
  tableUnavailableHint?: boolean
  /** Heading for this result set — set when the view renders one section of a sectioned answer. */
  label?: string
  /** Failure message for this result set, shown in place of (or above) the data when the backend
   *  couldn't produce this particular section. */
  sectionError?: string
}

function formatDateRange(summary?: AttendanceSummary): string | null {
  if (!summary?.dateFrom && !summary?.dateTo) return null
  if (summary.dateFrom && summary.dateTo) return `${summary.dateFrom} – ${summary.dateTo}`
  return summary.dateFrom || summary.dateTo || null
}

/** The scope sentence the backend composed, or null when it stated none.
 *
 *  Never reassembled from `branches`/`levels` here: the backend owns the wording (it knows whether
 *  the scope is full, narrowed by the portal's branch switcher, or widened on request) and two
 *  places composing the same sentence is two places for them to drift apart. */
function scopeLabel(scope?: AttendanceScope | null): string | null {
  const label = scope?.label?.trim()
  return label ? label : null
}

const AttendanceV2ResultView: React.FC<AttendanceV2ResultViewProps> = ({
  payload,
  theme,
  tableUnavailableHint = false,
  label,
  sectionError,
}) => {
  const intl = useIntl()
  const heading = label?.trim()
  const errorText = sectionError?.trim()
  const summary = payload.data?.summary
  const dateRange = formatDateRange(summary)
  const scope = scopeLabel(payload.data?.scope)
  // A scope the user did NOT choose — the portal's branch switcher narrowed it — is the one case
  // worth a caption: the reader may believe they are looking at the whole school.
  const scopeIsNarrowed = Boolean(payload.data?.scope?.narrowedByActiveContext)
  // The backend's call on whether scope changes how THIS answer reads (false for a named person's
  // own records). Absent on older payloads, which keep the narrowed-only rule.
  const scopeDisclosed = payload.data?.scope?.disclose !== false
  const {intent, columns, rows, nameLists} = getV2ResultTableData(payload)
  const showSummary =
    summary &&
    (intent === 'individual' ||
      intent === 'summary' ||
      intent === 'ranking' ||
      intent === 'roster' ||
      intent === 'pattern')
  const visualSummary = useMemo(() => buildV2AttendanceVisualSummary(payload), [payload])
  const showChart = hasV2AttendanceChart(visualSummary)
  const hasSummaryKpis = Boolean(
    showSummary && summary && shouldShowV2SummaryHeader(summary, rows.length, showChart)
  )
  // A table of nothing but zeros is not content. "No students are enrolled" followed by a card
  // holding a "Students 0" table, a scope caption and two export buttons was the reported defect:
  // the card had nothing to show and showed it anyway.
  const showTable = columns.length > 0 && rows.length > 0 && rowsHaveValues(rows)
  // A packed names column comes out of the table and is drawn as a list under it — see
  // `getV2ResultTableData`. When that column was all the table had, the list is the content.
  const showNameLists = nameLists.length > 0
  const hasContent = hasSummaryKpis || showChart || showTable || showNameLists
  const showDateRange = Boolean(dateRange) && hasContent
  // The full-scope caption ("Across all 10 campuses, covering all 37 levels — everything you have
  // access to") was noise on every card and was asked to go. Only a scope the reader did NOT
  // choose — narrowed by the branch switcher — is still worth a line on the card.
  const showScope = Boolean(scope) && hasContent && scopeIsNarrowed && scopeDisclosed

  // Requested items the backend found nothing for — shown apart from the rows so a partial answer
  // reads as partial. Only present when at least one item had no records.
  const noDataFor = useMemo(() => {
    const raw = payload.data?.stats?.noDataFor
    if (!Array.isArray(raw)) return []
    return raw
      .map((name) => (typeof name === 'string' ? name.trim() : ''))
      .filter((name): name is string => Boolean(name))
  }, [payload])
  const showNoData = noDataFor.length > 0

  const attendanceModeLabel = useMemo(
    () => resolveV2AttendanceModeLabel(payload, intl.formatMessage),
    [intl, payload]
  )

  const exportPayload = useMemo(
    () =>
      buildV2ResultExportPayload(payload, {
        defaultTitle: intl.formatMessage({id: 'CHATBOT.ATTENDANCE.EXPORT_REPORT_TITLE'}),
        attendanceModeLabel,
      }),
    [attendanceModeLabel, intl, payload]
  )

  const borderColor = theme === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'
  const cardBg = theme === 'dark' ? 'rgba(15, 23, 42, 0.94)' : 'rgba(255,255,255,0.65)'

  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_ROWS)

  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE_ROWS)
  }, [rows.length])

  const visibleRows = useMemo(() => rows.slice(0, visibleCount), [rows, visibleCount])
  const showLoadMore = rows.length > INITIAL_VISIBLE_ROWS && visibleCount < rows.length
  const remaining = rows.length - visibleCount
  const showLess = visibleCount > INITIAL_VISIBLE_ROWS
  const collapsible = Math.min(
    LOAD_MORE_BATCH,
    Math.min(visibleCount, rows.length) - INITIAL_VISIBLE_ROWS
  )

  const handleLoadMore = useCallback(() => {
    setVisibleCount((prev) => Math.min(prev + LOAD_MORE_BATCH, rows.length))
  }, [rows.length])

  const handleShowLess = useCallback(() => {
    setVisibleCount((prev) =>
      Math.max(Math.min(prev, rows.length) - LOAD_MORE_BATCH, INITIAL_VISIBLE_ROWS)
    )
  }, [rows.length])

  // Nothing to export when nothing is shown — a section that came back empty keeps its heading but
  // not a pair of buttons that would download a table of zeros.
  const showFooter =
    showLoadMore || showLess || (Boolean(exportPayload) && hasContent) || tableUnavailableHint

  // With no chart, no KPIs and no table there is nothing for a scope line, a "no data for" notice
  // or an export button to qualify — the narrative above already carries the answer. A section of
  // a fanned-out answer keeps its heading and error so the reader can see which part came back
  // empty, and the table hint stays because it is itself the explanation for the missing table.
  if (!hasContent && !heading && !errorText && !tableUnavailableHint) {
    return null
  }

  const headingColor = theme === 'dark' ? '#ffffff' : '#212529'
  const errorColor = theme === 'dark' ? '#f1416c' : '#d9214e'

  return (
    <div
      className='attendance-v2-result mt-2 mb-1 rounded-3 p-2'
      style={{
        border: `1px solid ${borderColor}`,
        backgroundColor: cardBg,
      }}
    >
      {heading ? (
        <div className='fw-semibold mb-1' style={{fontSize: '0.85rem', color: headingColor}}>
          {heading}
        </div>
      ) : null}
      {errorText ? (
        <div className='mb-2' style={{fontSize: '0.8rem', color: errorColor}}>
          {errorText}
        </div>
      ) : null}
      {showDateRange ? (
        <div
          className={showScope ? 'mb-1' : 'mb-2'}
          style={{
            fontSize: '0.75rem',
            color: theme === 'dark' ? 'rgba(255,255,255,0.75)' : '#6c757d',
          }}
        >
          {dateRange}
        </div>
      ) : null}
      {showScope ? (
        // An informational caption beside the date range, not a warning over the chart.
        <div
          className='mb-2 d-flex align-items-start gap-1'
          style={{
            fontSize: '0.75rem',
            color: theme === 'dark' ? 'rgba(255,255,255,0.75)' : '#6c757d',
          }}
        >
          <i
            className='bi bi-geo-alt'
            style={{fontSize: '0.7rem', lineHeight: 1.5}}
            aria-hidden='true'
          />
          <span>{scope}</span>
        </div>
      ) : null}
      {showChart && visualSummary ? (
        <AttendanceV2VisualSummary summary={visualSummary} theme={theme} />
      ) : hasSummaryKpis && summary ? (
        <AttendanceV2SummaryKpis summary={summary} theme={theme} rowCount={rows.length} />
      ) : null}
      {showTable ? (
        <AttendanceV2DataTable
          columns={columns}
          rows={visibleRows}
          totalRows={rows.length}
          theme={theme}
        />
      ) : tableUnavailableHint ? (
        <div
          className='mt-2'
          style={{
            fontSize: '0.8rem',
            color: theme === 'dark' ? 'rgba(255,255,255,0.65)' : '#6c757d',
          }}
        >
          {intl.formatMessage({id: 'CHATBOT.ATTENDANCE.TABLE_UNAVAILABLE'})}
        </div>
      ) : null}
      {showNameLists
        ? nameLists.map((list, idx) => (
            <AttendanceV2NameListSection
              key={`${list.title}-${idx}`}
              title={list.title}
              names={list.names}
              theme={theme}
            />
          ))
        : null}
      {showNoData ? (
        <AttendanceV2NoDataNotice
          title={intl.formatMessage(
            {id: 'CHATBOT.ATTENDANCE.NO_DATA_FOR_TITLE'},
            {count: noDataFor.length}
          )}
          names={noDataFor}
          theme={theme}
        />
      ) : null}
      {showFooter ? (
        <div className='d-flex justify-content-end align-items-center gap-1 mt-2 pt-1 flex-nowrap'>
          {showLess ? (
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              onClick={handleShowLess}
              style={{
                fontSize: '0.75rem',
                whiteSpace: 'nowrap',
                padding: '0.35rem 0.6rem',
              }}
            >
              {intl.formatMessage({id: 'CHATBOT.ATTENDANCE.SHOW_LESS'})}
              {` (${collapsible})`}
            </button>
          ) : null}
          {showLoadMore ? (
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              onClick={handleLoadMore}
              style={{
                fontSize: '0.75rem',
                whiteSpace: 'nowrap',
                padding: '0.35rem 0.6rem',
              }}
            >
              {intl.formatMessage({id: 'CHATBOT.ATTENDANCE.LOAD_MORE'})}
              {remaining > LOAD_MORE_BATCH ? ` (${LOAD_MORE_BATCH})` : ` (${remaining})`}
            </button>
          ) : null}
          {exportPayload ? (
            <AttendanceReportExportButtons exportPayload={exportPayload} theme={theme} inline />
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

export default AttendanceV2ResultView
